import { z } from 'zod';
import type { ConnectionUpdate } from './models.connections';

export const connectionSyncToTimelyEnums = ['on_demand', 'continuous'] as const;
export const ConnectionSyncTimelyEnumsSchema = z.enum(connectionSyncToTimelyEnums);

/**
 * Single source of truth for sync setting keys
 * All other sync-related objects derive from this.
 */
const supportedSyncsToTimelyTypes = [
  'sync_projects',
  'sync_tasks',
  'reference_tasks',
  'sync_tags',
  'sync_clients',
  'sync_teams',
  'sync_users',
  'sync_memories',
  'sync_time_entries',
] as const;

export const SupportedSyncToTimelyEnumsSchema = z.enum(supportedSyncsToTimelyTypes);
export type SupportedSyncToTimelyEnums = z.infer<typeof SupportedSyncToTimelyEnumsSchema>;

export const CustomFieldMappingTargetResourceTypeSchema = z.enum(['client']);
export type CustomFieldMappingTargetResourceType = z.infer<
  typeof CustomFieldMappingTargetResourceTypeSchema
>;

export const CustomFieldMappingContextValueSchema = z.union([z.string(), z.number(), z.boolean()]);

export const CustomFieldMappingContextSchema = z
  .record(z.string(), CustomFieldMappingContextValueSchema)
  .default({})
  .describe(
    'Provider-specific context required to read this field again during import; providers validate their executable context shape before using a mapping'
  );

export const CustomFieldMappingSchema = z.object({
  id: z.string().min(1).describe('Provider-scoped mapping identifier'),
  field_id: z.string().min(1).describe('Provider custom field identifier'),
  target_resource_type: CustomFieldMappingTargetResourceTypeSchema.describe(
    'The TIC resource type produced from this provider custom field'
  ),
  context: CustomFieldMappingContextSchema,
});
export type CustomFieldMapping = z.infer<typeof CustomFieldMappingSchema>;

export const CustomFieldMappingArraySchema = z.preprocess(
  value => (value == null ? [] : value),
  z.array(CustomFieldMappingSchema)
);

/**
 * Connection Settings are used to store end-user preferences and settings.
 * such as controlling access to data, what/how to sync with Timely, etc.
 *
 * @comment Please bump `version` by +1 when adding new properties to this schema.
 */
export type ConnectionSettings = z.infer<typeof ConnectionSettingsSchema>;
export const ConnectionSettingsSchema = z.object({
  version: z.coerce
    .number()
    .default(8)
    .describe(
      'Version of the connection settings schema; Increment this when making changes to the schema to simplify SQL migrations that backfill existing connections with new settings.'
    ),
  auto_subscribe: z.coerce.boolean().default(true).describe('Should new data be auto-subscribed?'),

  import_data_detail_level: z
    .enum(['default', 'minimum'])
    .default('default')
    .describe(
      'How much of the imported data should be stored? Set to minimum for only the essentials.'
    ),
  import_profile_key: z
    .string()
    .min(1)
    .default('default')
    .describe(
      'Profile key used to scope imported resources (e.g. Trello board vs. client hierarchy).'
    ),
  setup_completed: z.coerce
    .boolean()
    .default(false)
    .describe('Whether setup wizard is complete and data diff dumping to Timely should be active'),
  setup_settings_configured: z
    .boolean()
    .optional()
    .describe(
      'Internal wizard marker: whether initial sync settings defaults have been written to this connection. Used to avoid re-applying operation defaults when resuming setup.'
    ),
  sync_to_timely: ConnectionSyncTimelyEnumsSchema.nullable()
    .default('continuous')
    .describe(
      'How should the connection sync with Timely? `null` disables syncing (uninstalling any webhooks as well), `on_demand` has no webhooks and dumps once to Timely, `continuous` installs webhooks and creates content in Timely automatically'
    ),
  // Sync properties - ensures all SyncToTimelySettingKeys are included
  sync_projects: z.coerce.boolean().default(false).describe('Should projects sync to Timely?'),
  sync_tasks: z.coerce.boolean().default(false).describe('Should tasks be synced to Timely?'),
  reference_tasks: z.coerce
    .boolean()
    .default(false)
    .describe(
      'Should tasks be available as **references** when logging time? Does NOT require `sync_tasks` to be enabled. Timely tasks are not created.'
    ),
  sync_tags: z.coerce.boolean().default(false).describe('Should labels be synced to Timely?'),
  sync_clients: z.coerce.boolean().default(true).describe('Should clients sync to Timely?'),
  sync_teams: z.coerce.boolean().default(false).describe('Should teams sync to Timely?'),
  sync_users: z.coerce.boolean().default(false).describe('Should users sync to Timely?'),
  sync_memories: z.coerce.boolean().default(false).describe('Should memories be created in TIC?'),
  fallback_timely_client_id: z
    .number()
    .nullable()
    .default(null)
    .describe(
      'Default client ID to assign to imported projects when no client mapping is available'
    ),
  custom_field_mappings: CustomFieldMappingArraySchema.describe(
    'Provider custom fields that should produce or resolve TIC resource relationships'
  ),
  sync_time_entries: z.coerce
    .boolean()
    .default(false)
    .describe('Should time entries be synced back to the external system (e.g., Jira worklogs)?'),

  /**
   * Project sync options (flat settings schema).
   *
   * @remarks
   * These used to live under `sync_project_options.*`, but were never launched.
   * We now keep them flat to avoid nested settings handling across TIC + frontend.
   */
  project_add_all_workspace_users: z
    .boolean()
    .optional()
    .describe('Add all workspace users to imported projects'),
  project_replace_users_on_update: z
    .boolean()
    .optional()
    .describe('Replace vs merge project users on update'),
  project_send_invites: z
    .boolean()
    .optional()
    .describe('Send invites when adding users to projects'),
  project_require_labels: z
    .boolean()
    .optional()
    .describe('Set required_labels flag on imported projects'),
  project_auto_add_assignees_to_projects: z
    .boolean()
    .optional()
    .describe(
      'When tasks include assignees that are not members of the project in Timely, automatically add them to the project before importing the task.'
    ),
  project_default_rate_type: z
    .enum(['project', 'user', 'non-billable'])
    .optional()
    .describe('Default billing rate type for new projects'),
  restrict_tasks_to_assignees: z.coerce
    .boolean()
    .default(false)
    .describe(
      'When enabled, users only see tasks they have access to via their personal ClickUp connection (direct assignee, ancestor task assignee, or project member). Fail-closed: users without a personal connection see no tasks.'
    ),
});

/**
 * The settings a connection's initial setup starts from: the resolved import profile and the
 * provider's sync-setting defaults. A first setup treats a schema default as unset, so only an
 * explicitly saved non-default value survives; once `setup_settings_configured` is recorded, a
 * resumed setup fills only keys that were never stored.
 */
export function initialSetupSettings(
  raw: Record<string, unknown>,
  syncSettings: ReadonlyArray<{ key: string; defaultValue?: boolean }>,
  importProfileKey: string | null
): ConnectionSettings {
  const settings = ConnectionSettingsSchema.parse({ ...raw });
  if (importProfileKey) settings.import_profile_key = importProfileKey;
  const resuming = settings.setup_settings_configured === true;
  const schemaDefaults: Record<string, unknown> = ConnectionSettingsSchema.parse({});
  for (const { key, defaultValue } of syncSettings) {
    if (defaultValue === undefined || !(key in settings)) continue;
    const value = raw[key];
    if (value === undefined || value === null || (!resuming && value === schemaDefaults[key])) {
      Object.assign(settings, { [key]: defaultValue });
    }
  }
  if (
    settings.sync_tasks === true &&
    settings.project_auto_add_assignees_to_projects === undefined
  ) {
    settings.project_auto_add_assignees_to_projects = true;
  }
  return settings;
}

/** Whether a connection's setup is still initial: its settings have not recorded completion. */
export function isInitialSetup(
  settings: { setup_completed?: unknown } | null | undefined
): boolean {
  return !settings?.setup_completed;
}

/** The connection settings form's values, which it submits as the connection update body. */
export type ConnectionSettingsFormValues = Omit<ConnectionUpdate, 'id'>;

/** What the connection settings form opens a connection with. */
export interface ConnectionSettingsFormSetup {
  /** Whether the connection's setup is still initial (`isInitialSetup`). */
  initialSetup: boolean;
  /** The provider's sync settings, whose defaults only an initial setup applies. */
  syncSettings: ReadonlyArray<{ key: string; defaultValue?: boolean }>;
  /** The resolved import profile, which only an initial setup applies. */
  importProfileKey: string | null;
}

type ImportedDataAccess = ConnectionSettingsFormValues['imported_data_access'];

/** A connection without a saved access level shares its imported data with everyone. */
function importedDataAccessOf(
  connection: { imported_data_access?: ImportedDataAccess | null } | null | undefined
): ImportedDataAccess {
  return connection?.imported_data_access ?? 'everyone';
}

/**
 * The values the connection settings form opens with. An initial setup starts from
 * `initialSetupSettings`; after setup completes the profile is locked and provider defaults no
 * longer apply, so the form opens on the saved settings.
 */
export function connectionSettingsFormValues(
  connection:
    | {
        settings?: Record<string, unknown> | null;
        imported_data_access?: ImportedDataAccess | null;
      }
    | null
    | undefined,
  setup: ConnectionSettingsFormSetup
): ConnectionSettingsFormValues {
  const raw = connection?.settings ?? {};
  return {
    settings: setup.initialSetup
      ? initialSetupSettings(raw, setup.syncSettings, setup.importProfileKey)
      : ConnectionSettingsSchema.parse({ ...raw }),
    imported_data_access: importedDataAccessOf(connection),
  };
}

/**
 * What the connection settings form submits for its values. An initial setup records
 * `setup_settings_configured`, so a resumed setup keeps what it wrote; a custom field mapped to
 * clients requires client sync.
 */
export function connectionSettingsFormSubmission(
  values: ConnectionSettingsFormValues,
  initialSetup: boolean
): ConnectionSettingsFormValues {
  const settings = { ...values.settings };
  if (initialSetup) settings.setup_settings_configured = true;
  if (settings.custom_field_mappings?.some(mapping => mapping.target_resource_type === 'client')) {
    settings.sync_clients = true;
  }
  return { imported_data_access: values.imported_data_access, settings };
}

/** A sync-setting checkbox change in the connection settings form. */
export interface SyncSettingChoice<Setting> {
  /** The values the change sets, in the order the form sets them; none when it is refused. */
  changes: Array<
    [key: SupportedSyncToTimelyEnums | 'project_auto_add_assignees_to_projects', value: boolean]
  >;
  /** The settings the chosen one depends on that the change enabled with it. */
  enabledDependencies: SupportedSyncToTimelyEnums[];
  /** The enabled settings that depend on the chosen one; any of them refuses disabling it. */
  blockedBy: Setting[];
}

/**
 * Tick or untick one sync setting in the connection settings form. Enabling task creation also
 * turns on adding assignees to projects unless that choice was already made, and enabling a
 * setting enables the settings it depends on. Disabling a setting that an enabled setting depends
 * on is refused.
 */
export function chooseSyncSetting<
  Setting extends { key: string; dependant_on?: ReadonlyArray<SupportedSyncToTimelyEnums> },
>(
  settings: Partial<ConnectionSettings>,
  syncSettings: ReadonlyArray<Setting>,
  key: SupportedSyncToTimelyEnums,
  enabled: boolean
): SyncSettingChoice<Setting> {
  const values: Record<string, unknown> = settings;
  if (!enabled) {
    const blockedBy = syncSettings.filter(
      setting =>
        setting.dependant_on?.some(dependency => dependency === key) && values[setting.key] === true
    );
    return blockedBy.length > 0
      ? { changes: [], enabledDependencies: [], blockedBy }
      : { changes: [[key, false]], enabledDependencies: [], blockedBy: [] };
  }
  const changes: SyncSettingChoice<Setting>['changes'] = [];
  if (key === 'sync_tasks' && settings.project_auto_add_assignees_to_projects === undefined) {
    changes.push(['project_auto_add_assignees_to_projects', true]);
  }
  const chosen = new Map(syncSettings.map(setting => [setting.key, setting])).get(key);
  const enabledDependencies = (chosen?.dependant_on ?? []).filter(
    dependency => !values[dependency]
  );
  for (const dependency of enabledDependencies) changes.push([dependency, true]);
  changes.push([key, true]);
  return { changes, enabledDependencies, blockedBy: [] };
}

/**
 * What the setup wizard's connect step saves for a new connection before its setup completes:
 * the settings schema's defaults under the step's values (its import profile), with the
 * connection's access level.
 */
export function connectStepSubmission(
  connection: { imported_data_access?: ImportedDataAccess | null } | null | undefined,
  values: Record<string, unknown>
): ConnectionSettingsFormValues {
  return {
    imported_data_access: importedDataAccessOf(connection),
    settings: ConnectionSettingsSchema.parse(values),
  };
}

/** The provider's default import profile: the one flagged default, else its first profile. */
export function defaultImportProfileKey(
  importProfiles: ReadonlyArray<{ key: string; isDefault?: boolean }> | null | undefined
): string | null {
  if (!importProfiles || importProfiles.length === 0) return null;
  return importProfiles.find(profile => profile.isDefault)?.key ?? importProfiles[0]?.key ?? null;
}

/**
 * The import profile a connection uses. An account connection takes the profile chosen during
 * setup, then its saved profile; a personal connection ignores a choice and keeps its saved
 * profile. Both fall back to the provider's default profile.
 */
export function resolveImportProfileKey(params: {
  scope: 'personal' | 'account';
  selectedProfileKey?: string | null;
  connectionProfileKey?: string | null;
  defaultProfileKey?: string | null;
}): string | null {
  const { scope, selectedProfileKey, connectionProfileKey, defaultProfileKey } = params;
  if (scope === 'account') {
    return selectedProfileKey ?? connectionProfileKey ?? defaultProfileKey ?? null;
  }
  return connectionProfileKey ?? defaultProfileKey ?? null;
}
